/* eslint-disable react/react-in-jsx-scope */
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";

type IntroStatTone = "sand" | "olive" | "teal" | "blue" | "rose" | "slate";

export type ContributionTrackingStat = {
  key: string;
  label: string;
  value: number;
  suffix?: string;
  helper?: string;
  tone?: IntroStatTone;
};

export type ContributionCalendarEvent =
  | string
  | Date
  | {
      date: string | Date;
      count?: number;
      label?: string;
      targetId?: string;
      projectId?: number;
      amount?: number;
    };

export type ContributionCalendarDetailItem = {
  label: string;
  count: number;
  targetId?: string;
  projectId?: number;
  amount?: number;
};

export type ContributionReminderSection = "today" | "upcoming" | "overdue";

export type ContributionReminderItem = {
  id: string;
  label: string;
  date: string | Date;
  section: ContributionReminderSection;
  targetId?: string;
  projectId?: number;
  count?: number;
  amount?: number;
};

function format2(n: number): string {
  return Number(n || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getToneClasses(tone: IntroStatTone) {
  switch (tone) {
    case "sand":
      return "border-[#E9DBCA] bg-[#F7EFE6]";
    case "olive":
      return "border-[#D9D5BE] bg-[#F6F3E8]";
    case "teal":
      return "border-[#C7DFD8] bg-[#EAF7F3]";
    case "blue":
      return "border-[#C8D1E6] bg-[#ECF1FF]";
    case "rose":
      return "border-[#E6C8CD] bg-[#FFF0F2]";
    case "slate":
    default:
      return "border-slate-200 bg-white";
  }
}

function toDateKey(input: string | Date) {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function normalizeCalendarEvents(events: ContributionCalendarEvent[]) {
  const map = new Map<
    string,
    { total: number; details: ContributionCalendarDetailItem[] }
  >();

  for (const event of events || []) {
    const input = typeof event === "object" && "date" in event ? event.date : event;
    const countInput =
      typeof event === "object" && "count" in event ? Number(event.count ?? 1) : 1;
    const key = toDateKey(input);
    if (!key) continue;

    const count = Number.isFinite(countInput) && countInput > 0 ? countInput : 1;
    const label =
      typeof event === "object" && "label" in event
        ? String(event.label || "").trim() || "Événement"
        : "Événement";
    const amount =
      typeof event === "object" && "amount" in event
        ? Number(event.amount ?? 0) || 0
        : 0;

    const item = map.get(key) || { total: 0, details: [] };
    item.total += count;
    item.details.push({
      label,
      count,
      ...(typeof event === "object" && "targetId" in event
        ? { targetId: event.targetId }
        : {}),
      ...(typeof event === "object" && "projectId" in event
        ? { projectId: event.projectId }
        : {}),
      amount,
    });
    map.set(key, item);
  }

  return map;
}

function buildCalendarCells(cursor: Date) {
  const firstDay = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const daysInMonth = new Date(
    cursor.getFullYear(),
    cursor.getMonth() + 1,
    0,
  ).getDate();
  const firstWeekDayMonday0 = (firstDay.getDay() + 6) % 7;
  const totalCells = Math.ceil((firstWeekDayMonday0 + daysInMonth) / 7) * 7;

  const cells: Array<{ date: Date | null; day: number | null }> = [];
  for (let i = 0; i < totalCells; i += 1) {
    const day = i - firstWeekDayMonday0 + 1;
    if (day < 1 || day > daysInMonth) {
      cells.push({ date: null, day: null });
      continue;
    }
    cells.push({
      date: new Date(cursor.getFullYear(), cursor.getMonth(), day),
      day,
    });
  }
  return cells;
}

function parseDateOrNull(value: string | Date) {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatReminderDate(input: string | Date) {
  const date = parseDateOrNull(input);
  if (!date) return "Date inconnue";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function getReminderSectionLabel(section: ContributionReminderSection) {
  switch (section) {
    case "overdue":
      return "En retard";
    case "today":
      return "Aujourd'hui";
    case "upcoming":
    default:
      return "7 prochains jours";
  }
}

function getReminderSectionTone(section: ContributionReminderSection) {
  switch (section) {
    case "overdue":
      return {
        badge: "bg-rose-100 text-rose-700",
        border: "border-rose-200",
      };
    case "today":
      return {
        badge: "bg-amber-100 text-amber-700",
        border: "border-amber-200",
      };
    case "upcoming":
    default:
      return {
        badge: "bg-blue-100 text-blue-700",
        border: "border-blue-200",
      };
  }
}

function MiniCalendar({
  events,
  onCalendarEventClick,
}: {
  events: ContributionCalendarEvent[];
  onCalendarEventClick?: (item: ContributionCalendarDetailItem) => void;
}) {
  const [cursor, setCursor] = useState(() => {
    const first = events.find((item) => {
      const raw = typeof item === "object" && "date" in item ? item.date : item;
      return !!toDateKey(raw);
    });
    const firstDateRaw =
      typeof first === "object" && first && "date" in first ? first.date : first;
    const firstDate = firstDateRaw ? new Date(firstDateRaw) : new Date();
    if (Number.isNaN(firstDate.getTime())) return new Date();
    return new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
  });

  const eventMap = useMemo(() => normalizeCalendarEvents(events), [events]);
  const cells = useMemo(() => buildCalendarCells(cursor), [cursor]);
  const label = useMemo(() => {
    const raw = new Intl.DateTimeFormat("fr-FR", {
      month: "long",
      year: "numeric",
    }).format(cursor);
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }, [cursor]);

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4">
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-slate-100"
          onClick={() =>
            setCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
          }
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="text-sm font-semibold text-slate-800">{label}</div>
        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-slate-100"
          onClick={() =>
            setCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
          }
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-500">
        {["lu", "ma", "me", "je", "ve", "sa", "di"].map((w) => (
          <div key={w} className="py-1 font-medium">
            {w}
          </div>
        ))}
      </div>

      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((cell, index) => {
          if (!cell.date || !cell.day) {
            return <div key={`empty-${index}`} className="h-9 rounded-md" />;
          }
          const key = toDateKey(cell.date);
          const item = key ? eventMap.get(key) : undefined;
          const count = item?.total || 0;
          return (
            <div
              key={key || `day-${index}`}
              className="group relative flex h-9 flex-col items-center justify-center rounded-md"
            >
              <span className="text-xs text-slate-700">{cell.day}</span>
              {count > 0 ? (
                <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-rose-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-rose-400" />
                  {count}
                </span>
              ) : null}

              {count > 0 ? (
                <div className="absolute bottom-[110%] left-1/2 z-30 hidden w-72 -translate-x-1/2 rounded-lg border border-slate-200 bg-white p-2 text-left shadow-lg group-hover:block group-focus-within:block">
                  <div className="mb-1 text-[11px] text-slate-700">
                    <div className="font-semibold">Détail du jour</div>
                  </div>
                  <div className="max-h-52 space-y-1 overflow-y-auto pr-1">
                    {(item?.details || []).map((detail, idx) => (
                      <button
                        key={`${detail.label}-${detail.targetId || "na"}-${idx}`}
                        type="button"
                        className="flex w-full items-center justify-between rounded px-1 py-0.5 text-[11px] text-slate-600 hover:bg-slate-100"
                        onClick={() => onCalendarEventClick?.(detail)}
                      >
                        <span className="truncate pr-2 text-left">
                          {detail.label}
                        </span>
                        <span className="font-medium text-slate-800 whitespace-nowrap">
                          x{detail.count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReminderPanel({
  reminderItems,
  onReminderClick,
}: {
  reminderItems: ContributionReminderItem[];
  onReminderClick?: (item: ContributionReminderItem) => void;
}) {
  const grouped = useMemo(() => {
    const initial: Record<ContributionReminderSection, ContributionReminderItem[]> = {
      overdue: [],
      today: [],
      upcoming: [],
    };

    for (const item of reminderItems || []) {
      if (!item?.section || !initial[item.section]) continue;
      initial[item.section].push(item);
    }

    for (const section of Object.keys(initial) as ContributionReminderSection[]) {
      initial[section].sort((a, b) => {
        const da = parseDateOrNull(a.date)?.getTime() ?? 0;
        const db = parseDateOrNull(b.date)?.getTime() ?? 0;
        return da - db;
      });
    }

    return initial;
  }, [reminderItems]);

  const sections: ContributionReminderSection[] = ["overdue", "today", "upcoming"];

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-900">Rappels automatiques</div>
        <div className="text-xs text-slate-500">{reminderItems.length} rappel(s)</div>
      </div>

      <div className="space-y-2">
        {sections.map((section) => {
          const items = grouped[section] || [];
          const tone = getReminderSectionTone(section);
          return (
            <div key={section} className={`rounded-xl border p-2 ${tone.border}`}>
              <div className="mb-1 flex items-center justify-between gap-2">
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${tone.badge}`}
                >
                  {getReminderSectionLabel(section)}
                </span>
                <span className="text-[11px] font-medium text-slate-600">{items.length}</span>
              </div>

              {items.length === 0 ? (
                <div className="px-1 py-1 text-[11px] text-slate-400">Aucun rappel</div>
              ) : (
                <div className="max-h-44 space-y-1 overflow-y-auto pr-1">
                  {items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="w-full rounded-md px-2 py-1 text-left hover:bg-slate-100"
                      onClick={() => onReminderClick?.(item)}
                    >
                      <div className="truncate text-[11px] font-medium text-slate-800">
                        {item.label}
                      </div>
                      <div className="truncate text-[10px] text-slate-500">
                        {formatReminderDate(item.date)}
                        {Number(item.amount || 0) > 0
                          ? ` · ${format2(Number(item.amount || 0))} €`
                          : ""}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ContributionTrackingIntroPanel({
  title = "Suivi de contributions",
  subtitle = "Visualisez les contrats et les versements à traiter.",
  stats,
  calendarEvents,
  reminderItems,
  onCalendarEventClick,
  onReminderClick,
}: {
  title?: string;
  subtitle?: string;
  stats: ContributionTrackingStat[];
  calendarEvents: ContributionCalendarEvent[];
  reminderItems: ContributionReminderItem[];
  onCalendarEventClick?: (item: ContributionCalendarDetailItem) => void;
  onReminderClick?: (item: ContributionReminderItem) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">{title}</h2>
        <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {stats.map((stat) => (
            <div
              key={stat.key}
              className={`rounded-2xl border p-3 text-left ${getToneClasses(stat.tone || "slate")}`}
            >
              <div className="text-sm font-semibold text-slate-800">{stat.label}</div>
              <div className="mt-2 text-3xl font-semibold text-slate-900">
                {Number(stat.value || 0).toLocaleString("fr-FR")}
              </div>
              <div className="text-sm text-slate-500">{stat.suffix || "éléments"}</div>
              {stat.helper ? (
                <div className="mt-1 text-[11px] leading-snug text-slate-600">{stat.helper}</div>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        <MiniCalendar
          events={calendarEvents}
          onCalendarEventClick={onCalendarEventClick}
        />
        <ReminderPanel
          reminderItems={reminderItems}
          onReminderClick={onReminderClick}
        />
      </div>
    </div>
  );
}

